import api from '@/lib/api'
import { resolveRelativeApiPath } from '@/lib/serverContext'
import type { JobConnector, JobHandlers, JobResume } from '@/lib/jobs'

interface PtyJobOptions {
  /**
   * Oturum sona erdiğinde (doğal bitiş, hata veya bağlantı kopması) çağrılır —
   * biriken TÜM ham çıktı ve başarı durumuyla. Bu, JobsProvider'ın kendi
   * (global, router'ın ÜSTÜNDeki) state'ine bağlı bir closure olduğu için,
   * bu connector'ı başlatan React bileşeni (ör. AgentConsole) sayfa
   * değiştirildiği/kapandığı için unmount olsa bile ÇALIŞMAYA DEVAM EDER —
   * komut geçmişi kaydı gibi "oturum bitince bir şey yap" ihtiyaçları için.
   */
  onFinish?: (fullText: string, ok: boolean, returnCode?: number) => void
  /** Backend tarafından önceden açılmış bir PTY'ye yeniden bağlanır. */
  existingSessionId?: string
  /** Job metadata; sessionId is filled and persisted as soon as PTY opens. */
  resume?: JobResume
  /** Yeni PTY hazır olduğunda giriş satırına bir kez yazılır; Enter eklenmez. */
  initialInput?: string
}

/**
 * Agent'ın (compat_terminal.go) veya hub'ın kendi PTY oturum uçlarını
 * ({basePath}/sessions, {basePath}/commands — ikisi de aynı /sessions/{id}/
 * output|input|resize alt uçlarını paylaşır) bir interaktif Jobs bağlayıcısına
 * çevirir. Servers.tsx (sunucu konsolu) ve TerminalPage.tsx (hub-yerel kabuk)
 * bunu kullanır — böylece ikisi de küçültülebilir/arka-planda-devam-eden
 * ortak Jobs sistemine taşınmış olur.
 */
export function createPtyJobConnector(basePath: string, startPath: 'commands' | 'sessions', payload: Record<string, unknown>, opts: PtyJobOptions = {}): JobConnector {
  // Bind every request and persisted resume record to the opening server.
  // A later server-picker change must not redirect this live PTY.
  basePath = resolveRelativeApiPath(basePath)
  const resume = opts.resume ? { ...opts.resume, basePath } : undefined
  return (handlers: JobHandlers) => {
    let sessionId = opts.existingSessionId || ''
    // Reconnect sonrası localStorage'da tutulan cursor'dan devam et. Böylece
    // job.lines içinde zaten bulunan scrollback ikinci kez ekrana yazılmaz.
    let cursor = Number(resume?.cursor || 0)
    let pollHandle: number | null = null
    let heartbeatHandle: number | null = null
    let stopped = false
    let fullText = ''
    let finished = false
    let polling = false
    let pollFailures = 0
    let firstPollFailureAt = 0
    let inputBuffer = ''
    let inputTimer: number | null = null
    let resizeTimer: number | null = null
    let pendingResize: { rows: number; cols: number } | null = null
    let lastPollError = ''
    let initialInputSent = false

    function stopPolling() {
      if (pollHandle !== null) { window.clearInterval(pollHandle); pollHandle = null }
      if (heartbeatHandle !== null) { window.clearInterval(heartbeatHandle); heartbeatHandle = null }
    }
    function heartbeat() {
      if (sessionId && !stopped) api.post(`${basePath}/sessions/${sessionId}/heartbeat`).catch(() => {})
    }
    function flushInput() {
      if (inputTimer !== null) { window.clearTimeout(inputTimer); inputTimer = null }
      const data = inputBuffer
      inputBuffer = ''
      if (sessionId && data) api.post(`${basePath}/sessions/${sessionId}/input`, { data }).catch(() => {})
    }
    function flushResize() {
      if (resizeTimer !== null) { window.clearTimeout(resizeTimer); resizeTimer = null }
      const value = pendingResize
      pendingResize = null
      if (sessionId && value) api.post(`${basePath}/sessions/${sessionId}/resize`, value).catch(() => {})
    }
    function finishOnce(ok: boolean, returnCode?: number) {
      if (finished) return
      finished = true
      handlers.finish(ok)
      opts.onFinish?.(fullText, ok, returnCode)
    }

    async function poll() {
      if (!sessionId || stopped || polling) return
      polling = true
      try {
        const r = await api.get(`${basePath}/sessions/${sessionId}/output?cursor=${cursor}`)
        pollFailures = 0
        firstPollFailureAt = 0
        const chunk = String(r.data?.output || '')
        if (chunk) { fullText += chunk; handlers.appendLine(chunk) }
        const nextCursor = Number(r.data?.cursor ?? cursor)
        if (resume && nextCursor !== cursor) {
          handlers.setResume({ ...resume, sessionId, cursor: nextCursor })
        }
        cursor = nextCursor
        if (!r.data?.alive) {
          stopPolling()
          const returnCode = Number(r.data?.returncode ?? -1)
          finishOnce(returnCode === 0, returnCode)
        }
      } catch (e: any) {
        // Android suspends the WebView while an OAuth page is foregrounded.
        // A hidden-tab request failure is not evidence that the remote PTY died.
        if (document.visibilityState !== 'visible') return
        pollFailures += 1
        if (!firstPollFailureAt) firstPollFailureAt = Date.now()
        lastPollError = String(
          e?.response?.data?.detail ||
          e?.response?.data?.error ||
          e?.message ||
          'Terminal çıktısı alınamadı'
        )
        // Geçici ağ kopmalarında PTY'yi kapatma; sonraki polling turu
        // bağlantıyı kendiliğinden toparlar. 404, gerçek oturum kaybıdır.
        if (e?.response?.status === 404 || Date.now() - firstPollFailureAt >= 120_000) {
          stopPolling()
          if (e?.response?.status === 404) {
            handlers.appendLine('\r\nTerminal oturumu artık sunucuda bulunamıyor; yeni oturum başlatın.\r\n')
          } else {
            handlers.appendLine(`\r\nTerminal bağlantısı kurulamadı (${pollFailures} deneme): ${lastPollError}\r\n`)
          }
          finishOnce(false)
        }
      } finally {
        polling = false
      }
    }

    ;(async () => {
      try {
        if (!sessionId) {
          const r = await api.post(`${basePath}/${startPath}`, payload)
          sessionId = String(r.data?.session_id || '')
          if (!sessionId) throw new Error('Oturum kimliği alınamadı')
          if (r.data?.warning) handlers.appendLine(String(r.data.warning) + '\r\n')
        }
        if (resume && sessionId) handlers.setResume({ ...resume, sessionId })
        heartbeat()
        heartbeatHandle = window.setInterval(heartbeat, 15_000)
        await poll()
        if (!stopped && !opts.existingSessionId && opts.initialInput && !initialInputSent) {
          await api.post(`${basePath}/sessions/${sessionId}/input`, { data: opts.initialInput })
          initialInputSent = true
        }
        if (!stopped) pollHandle = window.setInterval(poll, 150)
      } catch (e: any) {
        const message = String(e?.response?.data?.detail || e?.response?.data?.error || e?.message || e)
        fullText += message
        handlers.appendLine(message)
        finishOnce(false)
      }
    })()

    return {
      cleanup: () => {
        stopped = true
        stopPolling()
        flushInput()
        flushResize()
        if (sessionId) api.delete(`${basePath}/sessions/${sessionId}`).catch(() => {})
      },
      sendInput: (data: string) => {
        inputBuffer += data
        if (inputTimer === null) inputTimer = window.setTimeout(flushInput, 20)
      },
      resize: (rows: number, cols: number) => {
        pendingResize = { rows, cols }
        if (resizeTimer === null) resizeTimer = window.setTimeout(flushResize, 40)
      },
    }
  }
}
